import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnDestroy,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { Subscription } from 'rxjs';

@Component({
  selector: 'itc-date-time-input',
  template: `
    <div class="date-time-input">
      @if (label || hasValue) {
        <div class="date-time-input__heading">
          @if (label) {
            <div class="date-time-input__label">
              {{ label }}
              @if (required && !hideRequiredMarker) {
                <span aria-hidden="true" class="mat-form-field-required-marker"
                  >*</span
                >
              }
            </div>
          }
          @if (hasValue) {
            <button
              mat-button
              type="button"
              [disabled]="disabled"
              [attr.aria-label]="clearAriaLabel"
              (click)="clear()"
            >
              Clear
            </button>
          }
        </div>
      }

      <div class="date-time-input__fields">
        <mat-form-field appearance="fill" subscriptSizing="dynamic">
          <mat-label>Date</mat-label>
          <input
            matInput
            readonly
            [id]="id + '-date'"
            [formControl]="dateControl"
            [matDatepicker]="datePicker"
            [min]="min"
            [max]="max"
            [attr.aria-label]="dateAriaLabel"
          />
          <mat-datepicker-toggle matIconSuffix [for]="datePicker" />
          <mat-datepicker #datePicker />
        </mat-form-field>

        <mat-form-field appearance="fill" subscriptSizing="dynamic">
          <mat-label>Time</mat-label>
          <input
            matInput
            readonly
            [id]="id + '-time'"
            [formControl]="timeControl"
            [ngxMatTimepicker]="timePicker"
            [format]="12"
            [attr.aria-label]="timeAriaLabel"
          />
          <ngx-mat-timepicker-toggle matIconSuffix [for]="timePicker" />
          <ngx-mat-timepicker
            #timePicker
            [format]="12"
            [minutesGap]="minutesGap"
          />
        </mat-form-field>
      </div>
    </div>
  `,
  styleUrls: ['./date-time-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimeInputComponent),
      multi: true,
    },
  ],
  imports: [
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    NgxMatTimepickerModule,
    ReactiveFormsModule,
  ],
})
export class DateTimeInputComponent implements ControlValueAccessor, OnDestroy {
  @Input() id = 'date-time-input';
  @Input() label?: string;
  @Input() required = false;
  @Input() hideRequiredMarker = false;
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;
  @Input() minutesGap = 5;
  @Input() disabled = false;

  readonly dateControl = new FormControl<Date | null>(null);
  readonly timeControl = new FormControl<string | null>(null);

  private readonly subscriptions = new Subscription();
  private onChange: (value: Date | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor() {
    this.subscriptions.add(
      this.dateControl.valueChanges.subscribe(() => this.emitCombinedValue())
    );
    this.subscriptions.add(
      this.timeControl.valueChanges.subscribe(() => this.emitCombinedValue())
    );
  }

  get hasValue(): boolean {
    return Boolean(this.dateControl.value || this.timeControl.value);
  }

  get dateAriaLabel(): string {
    return `${this.label || 'Date and time'} date`;
  }

  get timeAriaLabel(): string {
    return `${this.label || 'Date and time'} time`;
  }

  get clearAriaLabel(): string {
    return `Clear ${this.label || 'date and time'}`;
  }

  writeValue(value: unknown): void {
    const date = this.parseDate(value);
    this.dateControl.setValue(date, { emitEvent: false });
    this.timeControl.setValue(date ? this.formatTime(date) : null, {
      emitEvent: false,
    });
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (disabled) {
      this.dateControl.disable({ emitEvent: false });
      this.timeControl.disable({ emitEvent: false });
    } else {
      this.dateControl.enable({ emitEvent: false });
      this.timeControl.enable({ emitEvent: false });
    }
  }

  clear(): void {
    this.dateControl.setValue(null, { emitEvent: false });
    this.timeControl.setValue(null, { emitEvent: false });
    this.onChange(null);
    this.onTouched();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private emitCombinedValue(): void {
    const date = this.dateControl.value;
    const time = this.parseTime(this.timeControl.value);

    if (!date || !time) {
      this.onChange(null);
    } else {
      const combined = new Date(date);
      combined.setHours(time.hours, time.minutes, 0, 0);
      this.onChange(combined);
    }
    this.onTouched();
  }

  private parseDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    const date =
      value instanceof Date ? new Date(value) : new Date(String(value));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private formatTime(date: Date): string {
    const hours = date.getHours();
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')} ${period}`;
  }

  private parseTime(
    value: string | null
  ): { hours: number; minutes: number } | null {
    if (!value) {
      return null;
    }

    const match = value.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) {
      return null;
    }

    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
      return null;
    }

    if (match[3].toUpperCase() === 'PM' && hours !== 12) {
      hours += 12;
    } else if (match[3].toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }

    return { hours, minutes };
  }
}
